import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { site } from "@/content/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

export default function Shows() {
  const { upcoming, gallery, emptyStateTitle, emptyStateSub } = site.shows;

  return (
    <>
      <SEO
        title="Shows | EVE Band Live — Upcoming Shows & Booking"
        description="Feel the energy live — upcoming EVE show dates and the official live gallery. Book EVE, a modern rock band from Surabaya, Indonesia, for your stage."
      />
      <section data-testid="shows-page" className="pb-24 pt-32 lg:pb-32 lg:pt-40">
        <div className="container-eve">
          <Reveal>
            <p className="eyebrow mb-6">Shows</p>
            <h1 className="font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
              Feel the energy. <span className="gradient-text">Live.</span>
            </h1>
          </Reveal>

          {/* Upcoming */}
          <Reveal delay={0.1} className="mt-20">
            <h2 className="font-display text-3xl uppercase sm:text-4xl">
              Upcoming shows<span className="text-eve-cyan">.</span>
            </h2>
          </Reveal>
          {upcoming.length > 0 ? (
            <ul className="mt-10 divide-y divide-white/10 border-y border-white/10">
              {upcoming.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-4 py-6">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-eve-cyan">
                      {s.date}
                    </p>
                    <h3 className="mt-2 font-display text-2xl uppercase">{s.title}</h3>
                    <p className="mt-1 flex items-center gap-2 text-sm text-eve-silver">
                      <MapPin size={13} /> {s.location}
                    </p>
                  </div>
                  {s.ticketUrl && (
                    <a
                      href={s.ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline"
                      data-testid={`tickets-${s.id}`}
                    >
                      Tickets ↗
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div
              data-testid="upcoming-empty-state"
              className="mt-10 border border-white/10 bg-eve-surface/50 px-8 py-16 text-center sm:py-20"
            >
              <p className="font-display text-4xl uppercase leading-tight sm:text-5xl">
                {emptyStateTitle}
              </p>
              <p className="text-stroke mt-2 font-display text-4xl uppercase leading-tight sm:text-5xl">
                {emptyStateSub}
              </p>
            </div>
          )}

          {/* Live gallery */}
          <Reveal delay={0.05} className="mt-24">
            <h2 className="font-display text-3xl uppercase sm:text-4xl">
              Live gallery<span className="text-eve-cyan">.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((p, i) => (
              <Reveal key={p.src} delay={0.05 * (i % 3)}>
                <figure
                  data-testid={`live-photo-${i + 1}`}
                  className="group relative aspect-[4/3] overflow-hidden border border-white/10 bg-eve-surface"
                >
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </figure>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Booking CTA */}
        <div className="relative mt-28 overflow-hidden border-y border-white/10 py-20 lg:py-28">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(230,0,57,0.14),transparent_55%),radial-gradient(ellipse_at_85%_50%,rgba(0,217,255,0.12),transparent_55%)]" />
          <Reveal>
            <div className="container-eve relative flex flex-col items-center text-center">
              <p className="eyebrow mb-5">Booking</p>
              <h2 className="font-display text-4xl uppercase leading-[0.98] sm:text-5xl lg:text-6xl">
                Bring EVE
                <br />
                to your stage.
              </h2>
              <Link to="/contact" data-testid="book-eve-btn" className="btn-primary mt-10">
                Book EVE <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
